/**
 * Extract YouTube video ID from various URL formats
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/v/
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/ // Just the video ID
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
}

/**
 * Get YouTube thumbnail URL from video ID
 * Quality options: default (120x90), mqdefault (320x180), hqdefault (480x360), sddefault (640x480), maxresdefault (1280x720)
 * Frame options: 0, 1, 2, 3 (different auto-generated frames from the video)
 */
export function getYouTubeThumbnailUrl(
  videoId: string, 
  quality: 'default' | 'mqdefault' | 'hqdefault' | 'sddefault' | 'maxresdefault' = 'sddefault',
  frame?: number
): string {
  // If frame is specified (0-3), use the frame-based URL
  // Note: frame URLs don't support quality - they're always the same resolution
  if (frame !== undefined && frame >= 0 && frame <= 3) {
    return `https://img.youtube.com/vi/${videoId}/${frame}.jpg`;
  }
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Upgrade a YouTube thumbnail URL to higher resolution
 * Uses sddefault (640x480) which is available for all videos
 * maxresdefault (1280x720) is NOT available for all videos and shows a placeholder when missing
 */
/**
 * Upgrade a YouTube thumbnail URL to higher resolution
 * Always uses sddefault (640x480) which is available for all videos
 * Set preserveFrame=true to keep user-selected frame numbers (0-3) instead of upgrading
 */
export function upgradeYouTubeThumbnailQuality(thumbnailUrl: string, preserveFrame: boolean = false): string {
  if (!thumbnailUrl) return thumbnailUrl;
  
  // Check if it's a YouTube thumbnail URL
  const ytMatch = thumbnailUrl.match(/https?:\/\/img\.youtube\.com\/vi\/([a-zA-Z0-9_-]{11})\/(.+)\.jpg/);
  if (!ytMatch) return thumbnailUrl;
  
  const videoId = ytMatch[1];
  const qualityOrFrame = ytMatch[2];
  
  // If it's a frame number (0-3) and preserveFrame is true, keep it
  if (preserveFrame && /^[0-3]$/.test(qualityOrFrame)) {
    return thumbnailUrl;
  }
  
  // Default: always upgrade to sddefault (640x480) - guaranteed to exist
  if (['default', 'mqdefault', 'hqdefault'].includes(qualityOrFrame) || /^[0-3]$/.test(qualityOrFrame)) {
    return `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
  }
  
  return thumbnailUrl;
}

/**
 * Get YouTube embed URL from video ID
 */
export function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Check if a URL is a valid YouTube URL
 */
export function isYouTubeUrl(url: string): boolean {
  return extractYouTubeVideoId(url) !== null;
}
